<template>
  <WrapperFormControl
    :id="videoID"
    :required="required"
    :view-mode="disabled"
    :error="errors.first(videoID)"
    :label="label"
    :hide-label="hideLabel"
    :tooltip="tooltip"
  >
    <template slot="control">
      <EmbedBlock
        :embed="{ width, height }"
        class="upload-video-wrapper"
        :class="{ disabled, 'is-loading': isLoading }"
        @drop.native="onVideoDrop"
      >
        <InputFile
          :id="videoID"
          v-model="videoURL"
          v-validate="required ? 'required' : ''"
          :data-vv-name="videoID"
          :output-as="outputAs"
          :accept="formats"
          :name="videoID"
          :is-required="required"
          :disabled="disabled"
          :placeholder="placeholder"
          :class="{ 'is-empty': !videoURL }"
          class="upload-video-input"
          :drop-file-data="dropFileData"
          :hide-label="hideLabel"
          @filesSelected="isLoading = true"
          @filesUploaded="onVideoUploaded"
          @uploadError="onFileError"
        >
          <template slot="placeholder">
            <slot name="placeholder" />
          </template>
          <TextBlock slot="help" class="text-muted mt-3" :size="0.625">
            {{ help }}
          </TextBlock>
        </InputFile>
        <Icon
          v-if="videoURL && !isLoading"
          width="20"
          class="upload-video_delete cursor-pointer p-3"
          name="trash"
          :fill="$scss.iconPrimaryColor"
          hover-icon="trash-active"
          :hover-fill="$scss.iconPrimaryColor"
          @click="onClearField"
        />
        <div class="upload-video-preview">
          <VideoPreview
            v-if="videoURL"
            class="video-preview cursor-pointer"
            :src="videoURL"
          />
        </div>
      </EmbedBlock>
    </template>
  </WrapperFormControl>
</template>

<script>
const ACCEPTED_VIDEOS = ['mp4', 'avi', 'mov', '3gp', 'quicktime'];

import { mapActions } from 'vuex';
import EmbedBlock from '@/components/common/EmbedBlock';
import TextBlock from '@/components/atoms/TextBlock';
import InputFile from '@/components/form/HeraFormInputFile';
import { prop } from '@/utils/factories';
import Icon from '@/components/atoms/Icon';
import WrapperFormControl from '@/components/atoms/form/WrapperFormControl';
import VideoPreview from '@/components/atoms/VideoPreview';

export default {
  name: 'VideoUpload',
  components: {
    TextBlock,
    WrapperFormControl,
    EmbedBlock,
    InputFile,
    Icon,
    VideoPreview,
  },
  inject: ['$validator'],
  props: {
    value: prop(String),
    label: prop(String),
    name: prop(String),
    help: prop(String),
    tooltip: prop(String),
    placeholder: prop(String),

    required: prop(Boolean),
    outputAs: prop(String, 'binary'),
    formats: prop(Array, () => ACCEPTED_VIDEOS),

    width: prop(Number, 300),
    height: prop(Number, 300),
    disabled: prop(Boolean),
    hideLabel: prop(Boolean),
  },
  data() {
    return {
      dropFileData: null,
      isLoading: false,
    };
  },
  computed: {
    videoURL() {
      return this.value;
    },
    videoID() {
      return this.name || this.$lodash.uniqueId('video_');
    },
  },
  methods: {
    ...mapActions(['errorNotify']),
    onVideoUploaded(filesInfo) {
      this.isLoading = false;
      this.$emit('input', filesInfo[0]);
      this.errors.remove(this.videoID);
    },
    onFileError() {
      this.onClearField();
      this.errorNotify('_web_upload-video-fail-loading', 'Failed loading');
    },
    onClearField() {
      this.onVideoUploaded([null]);
    },
    onVideoDrop(e) {
      if (!this.disabled) {
        this.dropFileData = e;
      }
    },
  },
};
</script>

<style lang="scss">
.upload-video-wrapper {
  position: relative;
  border-radius: $ph-large-radius;

  .upload-video-input {
    position: absolute !important;
    width: 100%;
    height: 100%;
    opacity: 0.9;
    &.is-empty {
      background-image: url('~@/assets/images/background/upload-placeholder.png');
    }
    &:not(.is-empty) {
      opacity: 0;
    }
  }

  &:hover,
  &.is-loading {
    .upload-video-input {
      opacity: 0.9;
    }
  }

  .upload-video-preview {
    border: 1px solid $ph-secondary-text;
    border-radius: $ph-large-radius;
    width: 100%;
    height: 100%;
    overflow: hidden;
    z-index: 5;

    .video-preview {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    > * {
      height: 100%;
    }
  }
  .upload-video_delete {
    position: absolute;
    top: 0;
    @include auto-rtl(right, 0);
    z-index: 6;
  }

  &.disabled * {
    pointer-events: none;
    .upload-video-input {
      z-index: 6;
    }
  }
}
</style>
